import { objectEntries } from "@cervval.js/common/src/tools/object.tools";
import { i18n } from "./i18n";


/** Interface defining keys required in theme definition. Used for typing only */
export interface DefaultCjsThemeObjectTyping {
    "data-cjs-theme": never;
}

/** Default variable used in html to set a theme. Can be used alongside other theme variables to customize */
export type DefaultCjsThemeVariable = keyof DefaultCjsThemeObjectTyping;

/** Object describing a theme */
export type ThemeObject<ThemeObjectTyping extends DefaultCjsThemeObjectTyping = DefaultCjsThemeObjectTyping, ThemeVariables extends keyof ThemeObjectTyping = DefaultCjsThemeVariable> = {
    /** name used in application */
    displayName: string,
    /** Theme variables used for theme setting */
    variables: Record<ThemeVariables, string | null>
}

/** All themes available in application */
export type ThemeConfiguration<ThemeObjectTyping extends DefaultCjsThemeObjectTyping = DefaultCjsThemeObjectTyping, ThemeVariables extends keyof ThemeObjectTyping = DefaultCjsThemeVariable> = {
    [theme: string]: ThemeObject<ThemeObjectTyping, ThemeVariables>
}


/** Default usable themes */
export const defaultThemes = {
    // legacy theme used since cjs creation
    standard: {
        displayName: i18n().standard,
        variables: { "data-cjs-theme": "cjs-light-legacy" }
    },
    // cjs light theme
    light: {
        displayName: i18n().light,
        variables: { "data-cjs-theme": "cjs-light" }
    },
    // cjs dark theme
    dark: {
        displayName: i18n().dark,
        variables: { "data-cjs-theme": "cjs-dark" }
    }
} satisfies ThemeConfiguration;

/** Apply a given theme on document body */
export function applyTheme<ThemeObjectTyping extends DefaultCjsThemeObjectTyping = DefaultCjsThemeObjectTyping, ThemeVariables extends DefaultCjsThemeVariable = DefaultCjsThemeVariable>(theme: ThemeObject<ThemeObjectTyping, ThemeVariables>): void {
    const themeConfig = theme.variables;
    for (const [themeVariable, value] of objectEntries(themeConfig)) {
        if (value == null) { document.body.removeAttribute(themeVariable); }
        else { document.body.setAttribute(themeVariable, value); }
    }
}